/**
 * Attendance API Client (bible §66.3 / CaptainPad Attendance).
 *
 * CRITICAL (Section 8):
 * Zero client-side persistence or caching.
 * All requests bypass caches and interact directly with PostgreSQL backend.
 */
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api/attendance`;

const noCacheHeaders = (token) => ({
  Authorization: `Bearer ${token}`,
  "Cache-Control": "no-cache, no-store, must-revalidate",
  Pragma: "no-cache",
  Expires: "0",
});

export const getMyAttendanceStatus = async (token) => {
  const { data } = await axios.get(`${API}/my-status`, {
    headers: noCacheHeaders(token),
  });
  return data;
};

export const dutyIn = async (token) => {
  const { data } = await axios.post(`${API}/duty-in`, {}, { headers: noCacheHeaders(token) });
  return data;
};

export const startBreak = async (token, notes = null) => {
  const { data } = await axios.post(
    `${API}/break/start`,
    { notes },
    { headers: noCacheHeaders(token) }
  );
  return data;
};

export const endBreak = async (token) => {
  const { data } = await axios.post(
    `${API}/break/end`,
    {},
    { headers: noCacheHeaders(token) }
  );
  return data;
};

export const dutyOut = async (token, notes = null) => {
  const { data } = await axios.post(
    `${API}/duty-out`,
    { notes },
    { headers: noCacheHeaders(token) }
  );
  return data;
};

export const resumeDuty = async (token) => {
  const { data } = await axios.post(`${API}/resume`, {}, { headers: noCacheHeaders(token) });
  return data;
};

export const pauseForGap = async (token) => {
  const { data } = await axios.post(`${API}/gap/pause`, {}, { headers: noCacheHeaders(token) });
  return data;
};

export const postponeGap = async (token) => {
  const { data } = await axios.post(`${API}/gap/postpone`, {}, { headers: noCacheHeaders(token) });
  return data;
};

export const getMyHistory = async (token, fromDate = null, toDate = null) => {
  const params = {};
  if (fromDate) params.from_date = fromDate;
  if (toDate) params.to_date = toDate;
  const { data } = await axios.get(`${API}/my-history`, { headers: noCacheHeaders(token), params });
  return data;
};

// ── Day close (Register) ────────────────────────────────────────────────────

export const getDayClosePreview = async (token, businessDate = null) => {
  const params = businessDate ? { business_date: businessDate } : {};
  const { data } = await axios.get(`${API}/day-close/preview`, { headers: noCacheHeaders(token), params });
  return data;
};

export const closeAttendanceDay = async (token, businessDate, expectedIds) => {
  const { data } = await axios.post(
    `${API}/day-close`,
    { business_date: businessDate, expected_attendance_ids: expectedIds },
    { headers: noCacheHeaders(token) }
  );
  return data;
};

// ── Manager APIs ────────────────────────────────────────────────────────────

export const getManagerOverview = async (token, businessDate = null) => {
  const params = businessDate ? { business_date: businessDate } : {};
  const { data } = await axios.get(`${API}/manager/overview`, {
    headers: noCacheHeaders(token),
    params,
  });
  return data;
};

export const reconcileAttendance = async (token, payload) => {
  const { data } = await axios.post(`${API}/manager/reconcile`, payload, {
    headers: noCacheHeaders(token),
  });
  return data;
};

export const reviewOvertime = async (token, payload) => {
  const { data } = await axios.post(`${API}/manager/overtime`, payload, {
    headers: noCacheHeaders(token),
  });
  return data;
};

export const listShifts = async (token) => {
  const { data } = await axios.get(`${API}/shifts`, {
    headers: noCacheHeaders(token),
  });
  return data;
};

export const createShift = async (token, payload) => {
  const { data } = await axios.post(`${API}/shifts`, payload, {
    headers: noCacheHeaders(token),
  });
  return data;
};

export const updateShift = async (token, shiftId, payload) => {
  const { data } = await axios.put(`${API}/shifts/${shiftId}`, payload, {
    headers: noCacheHeaders(token),
  });
  return data;
};

export const deleteShift = async (token, shiftId) => {
  const { data } = await axios.delete(`${API}/shifts/${shiftId}`, {
    headers: noCacheHeaders(token),
  });
  return data;
};

export const assignUserShift = async (token, userId, shiftId) => {
  const { data } = await axios.put(
    `${API}/users/${userId}/shift`,
    { shift_id: shiftId },
    { headers: noCacheHeaders(token) }
  );
  return data;
};

export const getRecordEvents = async (token, attendanceId) => {
  const { data } = await axios.get(`${API}/manager/records/${attendanceId}/events`, { headers: noCacheHeaders(token) });
  return data;
};

export const createMissingRecord = async (token, payload) => {
  const { data } = await axios.post(`${API}/manager/records`, payload, { headers: noCacheHeaders(token) });
  return data;
};

// ── Workforce ───────────────────────────────────────────────────────────────

export const getWorkforce = async (token, params) => {
  const { data } = await axios.get(`${API}/workforce`, { headers: noCacheHeaders(token), params });
  return data;
};

// The CSV comes back as a blob and is saved straight away; nothing is kept.
export const exportWorkforce = async (token, params) => {
  const res = await axios.get(`${API}/workforce/export`, {
    headers: noCacheHeaders(token), params, responseType: "blob",
  });
  const url = URL.createObjectURL(res.data);
  const a = document.createElement("a");
  a.href = url;
  a.download = `attendance_${params.from_date}_${params.to_date}.csv`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
};

export const getPolicies = async (token) => {
  const { data } = await axios.get(`${API}/policies`, { headers: noCacheHeaders(token) });
  return data;
};

export const setOutletPolicy = async (token, values) => {
  const { data } = await axios.put(`${API}/policies/outlet`, values, { headers: noCacheHeaders(token) });
  return data;
};

export const setRolePolicy = async (token, roleId, values) => {
  const { data } = await axios.put(`${API}/policies/roles/${roleId}`, { values }, { headers: noCacheHeaders(token) });
  return data;
};

export const setUserOverrides = async (token, userId, values, reviewed = false) => {
  const { data } = await axios.put(`${API}/policies/users/${userId}`, { values, reviewed }, { headers: noCacheHeaders(token) });
  return data;
};
